import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "statusFilter",
    "statusSelect",
    "tableBody",
    "loadingIndicator",
    "scrollTrigger"
  ]

  connect() {
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMore = false;
    
    if (this.hasScrollTriggerTarget) {
      const trigger = this.scrollTriggerTarget;
      this.currentPage = parseInt(trigger.dataset.currentPage) || 1;
      this.totalPages = parseInt(trigger.dataset.totalPages) || 1;
      this.studentProgramId = trigger.dataset.studentProgramId;
      this.hasMore = this.currentPage < this.totalPages;
      
      if (this.hasMore && this.studentProgramId) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && this.hasMore && !this.isLoading) {
              this.loadNextPage();
            }
          })
        }, {
          rootMargin: '200px', // Start loading more 200px before reaching the trigger
          threshold: 0.1 // Trigger when 10% of the element is visible
        })
        
        this.observer.observe(trigger);
      }
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  disableProcessCriteriaButton(event) {
    const form = event.target;
    const btn = form.querySelector('input[type=submit], button[type=submit]');
    if (btn) {
      btn.disabled = true;
    }
  }

  filter() {
    const selectedStatus = this.hasStatusFilterTarget ? this.statusFilterTarget.value : "";
    
    // Reload the page with the status filter to get filtered results from server
    const url = new URL(window.location.href);
    if (selectedStatus) {
      url.searchParams.set('status', selectedStatus);
    } else {
      url.searchParams.delete('status');
    }
    url.searchParams.delete('page'); // Reset to page 1 when filtering
    
    window.location.href = url.toString();
  }

  statusChanged(event) {
    const select = event.target
    const enrollmentId = select.dataset.enrollmentId
    const newStatus = select.value

    if (!enrollmentId) return

    const row = select.closest("tr")
    const previousStatus = row?.dataset.status

    if (row) row.dataset.status = newStatus

    fetch(`/schools/reports/student-programs/${enrollmentId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({ enrollment: { status: newStatus } })
    })
      .then(response => {
        if (!response.ok) throw new Error("Failed to update status")
        return response.json()
      })
      .then(data => {
        if (row && data.status) {
          row.dataset.status = data.status
          select.value = data.status
        }
        if (data.actions_html) {
          const actionsContainer = document.getElementById(`student_program_certificate_actions_${enrollmentId}`)
          if (actionsContainer) {
            actionsContainer.outerHTML = data.actions_html
          }
        }
        this.showToast("Status changed")
      })
      .catch(error => {
        console.error(error)
        if (row && previousStatus) {
          row.dataset.status = previousStatus
          select.value = previousStatus
        }
        this.showToast("Could not update status")
      })
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta && meta.content
  }

  showToast(message) {
    window.dispatchEvent(new CustomEvent("toast:show", {
      detail: { message }
    }));
  }

  async showCopyQuickLook(event) {
    event.preventDefault();

    const enrollmentId = event.currentTarget.dataset.enrollmentId;

    if (!enrollmentId) {
      console.error("Enrollment ID not found");
      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(`/schools/reports/student-programs/copy-quick-look/${enrollmentId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    });

    if (response.ok) {
      const quickViewElement = document.querySelector("#student-program-copy-quick-look");
      
      if (!quickViewElement) {
        console.error("Quick view element not found in the DOM.");
        return;
      }

      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      Turbo.renderStreamMessage(responseBody);
    } else {
      console.error("Failed to load quick look:", response.statusText);
    }
  }

  closeCopyQuickLook(event) {
    event.preventDefault();
    const quickViewElement = document.querySelector("#student-program-copy-quick-look");
    if (quickViewElement) {
      quickViewElement.classList.add("u-hidden");
    }
  }

  async loadNextPage() {
    if (this.isLoading || !this.hasMore) return;
    
    this.isLoading = true;
    this.currentPage += 1;
    
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.style.display = 'block';
    }
    
    try {
      const selectedStatus = this.hasStatusFilterTarget ? this.statusFilterTarget.value : "";
      const statusParam = selectedStatus ? `&status=${encodeURIComponent(selectedStatus)}` : "";
      
      const url = `/schools/reports/student-programs/${this.studentProgramId}/page?page=${this.currentPage}${statusParam}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'X-CSRF-Token': this.csrfToken
        }
      })
      
      if (!response.ok) {
        throw new Error(`Failed to load page: ${response.status} ${response.statusText}`);
      }
      
      const html = await response.text();
      const tempTable = document.createElement('table');
      const tempTableBody = document.createElement('tbody');
      tempTableBody.innerHTML = html.trim();
      tempTable.appendChild(tempTableBody);
      
      const rows = Array.from(tempTableBody.querySelectorAll('tr'));
      
      if (rows.length === 0) {
        this.hasMore = false;
        if (this.hasScrollTriggerTarget) {
          this.scrollTriggerTarget.style.display = 'none';
        }
      } else {
        if (this.hasTableBodyTarget) {
          rows.forEach(row => {
            this.tableBodyTarget.appendChild(row);
          })
        } else {
          console.error('Table body target not found!');
        }
        
        if (rows.length < 50) {
          this.hasMore = false;
          if (this.hasScrollTriggerTarget) {
            this.scrollTriggerTarget.style.display = 'none';
          }
        } else {
          if (this.hasScrollTriggerTarget) {
            this.scrollTriggerTarget.dataset.currentPage = this.currentPage;
          }
        }
      }
    } catch (error) {
      console.error('Error loading next page:', error);
      this.currentPage -= 1
      this.showToast("Could not load more students");
    } finally {
      this.isLoading = false;
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.style.display = 'none';
      }
    }
  }
}
